"use client";

import * as React from "react";
import { useRef, useCallback } from "react";
import Image from "next/image";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useKeyboardNavigation } from "../../../hooks/gallery-lightbox/use-keyboard-navigation";

export type GalleryImageT = {
  src: string;
  alt?: string;
  title?: string;
};

type GalleryLightboxDesktopPropsT = {
  images: GalleryImageT[];
  initialIndex: number;
  onClose: () => void;
};

/**
 * Desktop-optimized gallery lightbox component
 * Features keyboard and button navigation with thumbnail preview
 */
export function GalleryLightboxDesktop({
  images,
  initialIndex,
  onClose,
}: GalleryLightboxDesktopPropsT) {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const handleThumbnailClick = useCallback((index: number) => {
    setCurrentIndex(index);
    thumbnailRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, []);

  useKeyboardNavigation({
    isEnabled: true,
    onEscape: onClose,
    onArrowLeft: handlePrevious,
    onArrowRight: handleNext,
  });

  return (
    <div className="animate-in fade-in fixed inset-0 z-[150] flex flex-col bg-black duration-300">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-all hover:scale-110 hover:bg-white/20 active:scale-95"
        aria-label="Close gallery"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Main image area */}
      <div className="relative flex flex-1 items-center justify-center px-16 py-4">
        {/* Previous button */}
        {images.length > 1 && (
          <button
            onClick={handlePrevious}
            className="absolute left-4 z-40 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-all hover:scale-110 hover:bg-white/20 active:scale-95"
            aria-label="Previous image"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}

        {/* Current image - NO key prop to prevent re-mounting */}
        <div className="relative h-full w-full max-w-6xl">
          {images.map((image, index) => (
            <div
              key={image.src}
              className={cn(
                "absolute inset-0 transition-opacity duration-300",
                index === currentIndex
                  ? "opacity-100"
                  : "pointer-events-none opacity-0"
              )}
            >
              <Image
                src={image.src}
                alt={image.alt || ""}
                fill
                className="object-contain"
                sizes="(max-width: 1200px) 90vw, 80vw"
                priority={
                  index === currentIndex ||
                  index === currentIndex - 1 ||
                  index === currentIndex + 1
                }
                quality={90}
              />
            </div>
          ))}
        </div>

        {/* Next button */}
        {images.length > 1 && (
          <button
            onClick={handleNext}
            className="absolute right-4 z-40 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-all hover:scale-110 hover:bg-white/20 active:scale-95"
            aria-label="Next image"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* Thumbnail navigation */}
      {images.length > 1 && (
        <div
          className="flex justify-center gap-3.5 overflow-x-auto scroll-smooth bg-black p-4"
          style={{
            scrollSnapType: "x proximity",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {images.map((image, index) => (
            <button
              key={image.src}
              ref={(el) => {
                thumbnailRefs.current[index] = el;
              }}
              onClick={() => handleThumbnailClick(index)}
              className={cn(
                "relative h-16 w-24 flex-shrink-0 overflow-hidden rounded-md transition-all duration-300",
                currentIndex === index
                  ? "scale-110 opacity-100 ring-2 ring-white ring-offset-2 ring-offset-black"
                  : "scale-100 opacity-60 hover:opacity-100"
              )}
              style={{ scrollSnapAlign: "center" }}
              aria-label={`Go to image ${index + 1}`}
            >
              <Image
                src={image.src}
                alt={image.alt || `Thumbnail ${index + 1}`}
                fill
                className="object-cover"
                sizes="96px"
              />
            </button>
          ))}
        </div>
      )}

      {/* Image counter */}
      <div className="absolute bottom-4 left-4 rounded-full bg-black/50 px-3 py-1 text-sm text-white backdrop-blur">
        {currentIndex + 1} / {images.length}
      </div>
    </div>
  );
}
